import { Injectable, computed, signal } from '@angular/core';
import { NearbyStore } from './store.models';
import { PublicStoreType } from './store-type.models';
import { API_BASE, resolveImageUrl } from './ordering-api';

/** Something of ours worth picturing, and the artwork to picture it by. */
export interface ShopPicture {
  /** Stable across rebuilds, so a picture that failed stays out. */
  key: string;
  src: string;
  /** A logo is a brand mark of unknown proportion and must be fitted, not cropped. */
  isMark: boolean;
}

/**
 * The shops and shop-type artwork the browse pages have ALREADY fetched, published for anything
 * else in the layout that wants to picture them — today the "get the app" popup, which shows real
 * shops inside its drawn phone instead of coloured blocks.
 *
 * A plain relay: it never calls the API itself, so picturing them costs no second request and can
 * never disagree with the listing the shopper is looking at. Whoever owns the data pushes it in;
 * everywhere else the list is empty and the caller falls back to its own artwork.
 */
@Injectable({ providedIn: 'root' })
export class NearbyShowcaseService {
  private readonly stores = signal<readonly NearbyStore[]>([]);
  private readonly types = signal<readonly PublicStoreType[]>([]);

  publishStores(stores: readonly NearbyStore[]): void {
    this.stores.set(stores);
  }

  publishTypes(types: readonly PublicStoreType[]): void {
    this.types.set(types);
  }

  /**
   * Whether the browse page behind has any shops at all. Not the same question as [pictures] being
   * non-empty — an area can have shops whose artwork all failed — and it is the honest test of
   * "is there anything here worth interrupting for".
   */
  readonly hasShops = computed(() => this.stores().length > 0);

  /**
   * What to picture, best first:
   *
   *  1. **Shop-type artwork** — the platform's own food photography behind the category tiles.
   *     Commissioned to be looked at, so it is presentable at any size and in any area, which is
   *     what a promotional picture has to be.
   *  2. **Shop banners** — a photograph of the shopper's own nearby shop or its food. More telling
   *     than any stock picture when a shop has a good one, but plenty do not, so it comes second.
   *  3. **Shop logos** — real, but a brand mark rather than a picture of anything, and some are a
   *     dark block that reads as a broken image at thumbnail size. Hence last.
   *
   * Shop logos are NOT gated on `onlineLogoUrl`: the nearby payload leaves that field empty even
   * for shops that have one, which is why the shop cards ask for the logo URL outright and handle
   * the 404. Same here — the caller drops a picture that does not load and takes the next.
   */
  readonly pictures = computed<readonly ShopPicture[]>(() => {
    const hasBanner = (s: NearbyStore) => (s.onlineBannerUrl ?? '').trim().length > 0;
    const stores = this.stores();
    const types = this.types();

    const shopPicture = (endpoint: 'store-banner' | 'store-logo') => (s: NearbyStore): ShopPicture => ({
      key: `${endpoint}:${s.id}`,
      src: `${API_BASE}/api/public/${endpoint}/${s.id}`,
      isMark: endpoint === 'store-logo',
    });
    // Sub-categories before their parents: "Bakery" and "Fruit & Vegetables" picture food, while a
    // top-level "Shop" is as generic as its name.
    const tiles = [...types.flatMap((t) => t.subCategories ?? []), ...types]
      .map((t) => ({ key: `type:${t.id}`, src: resolveImageUrl(t.imageUrl) ?? '', isMark: false }))
      .filter((p) => p.src.length > 0);

    const all = [
      ...tiles,
      ...stores.filter(hasBanner).map(shopPicture('store-banner')),
      ...stores.filter((s) => !hasBanner(s)).map(shopPicture('store-logo')),
    ];
    // The same artwork can sit on several categories; showing it twice looks like a mistake.
    const seen = new Set<string>();
    return all.filter((p) => !seen.has(p.src) && seen.add(p.src));
  });
}
